import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Loader2, Sparkles } from 'lucide-react';

import { MathTextBlock } from '../../components/MathTextBlock';
import { ProxyImage } from '../../components/assets/ProxyImage';
import { useAuth } from '../../context/AuthContext';
import {
  dajiaoService,
  type GeometryCandidate,
  type GeometryGeneratorId,
} from '../../services/dajiaoService';
import { assetService } from '../../services/assetService';
import { factoryService, type FactoryStructuralUnit } from '../../services/factoryService';
import {
  figureKindLabelZh,
  isConfirmedPrintedFigureQa,
  structuralUnitExtractQa,
  type ExtractQaItem,
} from '../utils/extractQaPreview';
import { cropPageImageByBox } from '../utils/figureCropUtils';
import {
  readParticleEmbedImageKind,
  resolveModelOrder,
  type EmbedImageKind,
} from '../../utils/geometryVendorKinds';

/** 走马灯可选生图模块（按 GT/NGT 过滤展示） */
export const FIGURE_EDIT_PROVIDER_CATALOG: Record<
  GeometryGeneratorId,
  { id: GeometryGeneratorId; label: string; blurb: string }
> = {
  'dajiao-qwen-agent': {
    id: 'dajiao-qwen-agent',
    label: '大角 · Qwen Agent（DQA）',
    blurb: 'GT · 几何生图两阶段（Qwen 规划 + 大角 MCP）',
  },
  dajiao: {
    id: 'dajiao',
    label: '大角智能生图（DJG）',
    blurb: 'GT · 大角几何生图',
  },
  'gpt-image-2': {
    id: 'gpt-image-2',
    label: 'GPT Image 2（WaveSpeed）',
    blurb: 'NGT · OpenAI edit · 参考题图清脏 / 结构还原',
  },
  'qwen-image-3.0': {
    id: 'qwen-image-3.0',
    label: 'Qwen Image 3.0',
    blurb: '已下线 · 请用 Pro',
  },
  'qwen-image-3.0-pro': {
    id: 'qwen-image-3.0-pro',
    label: 'Qwen Image 3.0 Pro',
    blurb: 'NGT · 通义旗舰档 · 参考题图清脏 / 与 OI2 对照',
  },
};

export type FigureEditProviderId = GeometryGeneratorId;

export type FigureExtractCandidate = {
  key: string;
  unitId: string;
  unitIndex: number;
  qaIndex: number;
  question: string;
  figureKind?: string;
  figureNote?: string;
  figureBox?: ExtractQaItem['figureBox'];
};

type TrialSlot = {
  status: 'idle' | 'running' | 'ready' | 'error';
  pngUrl?: string;
  error?: string;
  costCny?: number | null;
  costEstimated?: boolean;
  elapsedMs?: number;
};

const ALL_PROVIDER_IDS = Object.keys(FIGURE_EDIT_PROVIDER_CATALOG) as GeometryGeneratorId[];

/** 仅当前任务 batch 下、提取确认的印刷配图（可切块）。 */
function collectFigureCandidates(
  units: FactoryStructuralUnit[],
  batchId: string,
): FigureExtractCandidate[] {
  const out: FigureExtractCandidate[] = [];
  const bid = String(batchId || '').trim();
  units.forEach((su, suIdx) => {
    if (su.extractIgnored || su.extractStatus === 'ignored') return;
    // 硬约束：结构单元必须归属当前任务
    const suBatch = String(su.batchId || '').trim();
    if (suBatch && bid && suBatch !== bid) return;
    const qaList = structuralUnitExtractQa(su);
    qaList.forEach((qa, qi) => {
      if (!isConfirmedPrintedFigureQa(qa)) return;
      out.push({
        key: `${su.id}:${qi}`,
        unitId: su.id,
        unitIndex: Number(su.unitIndex) || suIdx,
        qaIndex: qi,
        question: qa.question || '',
        figureKind: qa.figureKind,
        figureNote: qa.figureNote,
        figureBox: qa.figureBox,
      });
    });
  });
  return out;
}

async function dataUrlToJpegFile(dataUrl: string, filename: string): Promise<File> {
  const res = await fetch(dataUrl);
  const blob = await res.blob();
  return new File([blob], filename, { type: blob.type || 'image/jpeg' });
}

function emptySlots(): Record<GeometryGeneratorId, TrialSlot> {
  return {
    'dajiao-qwen-agent': { status: 'idle' },
    dajiao: { status: 'idle' },
    'gpt-image-2': { status: 'idle' },
    'qwen-image-3.0': { status: 'idle' },
    'qwen-image-3.0-pro': { status: 'idle' },
  };
}

function slotsStorageKey(batchId: string): string {
  return `psycheflow.factoryFigureSlots.v1.${batchId}`;
}

function readCachedSlots(
  batchId: string,
  candidateKey: string,
): Record<GeometryGeneratorId, TrialSlot> | null {
  try {
    const raw = sessionStorage.getItem(slotsStorageKey(batchId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<string, Record<string, TrialSlot>>;
    const hit = parsed?.[candidateKey];
    if (!hit || typeof hit !== 'object') return null;
    const next = emptySlots();
    for (const id of ALL_PROVIDER_IDS) {
      const slot = hit[id];
      if (slot?.status === 'ready' && slot.pngUrl) {
        next[id] = {
          status: 'ready',
          pngUrl: String(slot.pngUrl),
          costCny: slot.costCny ?? null,
          costEstimated: Boolean(slot.costEstimated),
          elapsedMs: slot.elapsedMs,
        };
      }
    }
    return next;
  } catch {
    return null;
  }
}

function writeCachedSlot(
  batchId: string,
  candidateKey: string,
  providerId: GeometryGeneratorId,
  slot: TrialSlot,
): void {
  try {
    const raw = sessionStorage.getItem(slotsStorageKey(batchId));
    const parsed = (raw ? JSON.parse(raw) : {}) as Record<string, Record<string, TrialSlot>>;
    const row = { ...(parsed[candidateKey] || {}) };
    row[providerId] = {
      status: slot.status,
      pngUrl: slot.pngUrl,
      costCny: slot.costCny,
      costEstimated: slot.costEstimated,
      elapsedMs: slot.elapsedMs,
    };
    parsed[candidateKey] = row;
    sessionStorage.setItem(slotsStorageKey(batchId), JSON.stringify(parsed));
  } catch {
    // ignore
  }
}

function applyCandidateSlots(
  list: Array<{ id?: string; pngUrl?: string | null; costCny?: number | null; costEstimated?: boolean; elapsedMs?: number }>,
): Record<GeometryGeneratorId, TrialSlot> {
  const next = emptySlots();
  for (const id of ALL_PROVIDER_IDS) {
    const hit = list.find((item) => item.id === id && item.pngUrl);
    if (hit?.pngUrl) {
      next[id] = {
        status: 'ready',
        pngUrl: String(hit.pngUrl),
        costCny: hit.costCny ?? null,
        costEstimated: Boolean(hit.costEstimated),
        elapsedMs: hit.elapsedMs,
      };
    }
  }
  return next;
}

type Props = {
  batchId: string;
  structuralUnits: FactoryStructuralUnit[];
  /** 路由标定：结构单元 id */
  selectedUnitId?: string | null;
  /** 路由标定：题号（0-based） */
  selectedQaIndex?: number | null;
  /** 选中颗粒时回写路由，便于与「颗粒提取」对照 */
  onSelectParticle?: (unitId: string, qaIndex: number) => void;
};

/**
 * 插图提取工序结果台：
 * 原切块预览 + 生图走马灯（按学习侧 GT/NGT 分类切换模块）。
 */
export function FigureExtractPhasePanel({
  batchId,
  structuralUnits,
  selectedUnitId = null,
  selectedQaIndex = null,
  onSelectParticle,
}: Props) {
  const { user } = useAuth();
  const candidates = useMemo(
    () => collectFigureCandidates(structuralUnits, batchId),
    [structuralUnits, batchId],
  );
  const [localKey, setLocalKey] = useState<string | null>(null);
  const listItemRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  const selectCandidate = (c: FigureExtractCandidate) => {
    if (onSelectParticle) onSelectParticle(c.unitId, c.qaIndex);
    else setLocalKey(c.key);
  };
  const [cropStatus, setCropStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [cropError, setCropError] = useState('');
  const [cropDataUrl, setCropDataUrl] = useState('');
  const [cropMeta, setCropMeta] = useState<{
    pageW: number;
    pageH: number;
    crop: { x: number; y: number; w: number; h: number };
    boxUsed?: { x: number; y: number; w: number; h: number };
  } | null>(null);
  const [slots, setSlots] = useState<Record<GeometryGeneratorId, TrialSlot>>(emptySlots);
  const uploadedRefByKey = useRef<Map<string, { imageUrl: string; ossId?: string }>>(new Map());
  const particleIdByKey = useRef<Map<string, string>>(new Map());
  const runTokenByProvider = useRef<Partial<Record<GeometryGeneratorId, number>>>({});
  const [boundParticleId, setBoundParticleId] = useState<string | null>(null);
  const [embedKind, setEmbedKind] = useState<EmbedImageKind | null>(null);
  const [classifying, setClassifying] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const classifyTokenRef = useRef(0);

  const runEmbedClassifier = async (particleId: string) => {
    const token = ++classifyTokenRef.current;
    setClassifying(true);
    try {
      const data = await dajiaoService.classifyEmbedImage({ particleId });
      if (classifyTokenRef.current !== token) return;
      const kind = data.result?.kind
        || readParticleEmbedImageKind(
          (data.particle as { metadata?: Record<string, unknown> } | undefined)?.metadata,
        );
      if (kind === 'GT' || kind === 'NGT') {
        setEmbedKind(kind);
        setCarouselIndex(0);
      }
    } catch (err) {
      console.warn(
        '[figure-extract] classify failed',
        err instanceof Error ? err.message : err,
      );
    } finally {
      if (classifyTokenRef.current === token) setClassifying(false);
    }
  };

  // restore particle binding cache from session
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(`psycheflow.factoryFigureParticle.v1.${batchId}`);
      if (!raw) return;
      const parsed = JSON.parse(raw) as Record<string, string>;
      if (parsed && typeof parsed === 'object') {
        for (const [k, v] of Object.entries(parsed)) {
          if (k && v) particleIdByKey.current.set(k, v);
        }
      }
    } catch {
      // ignore
    }
  }, [batchId]);

  const selected = useMemo(() => {
    if (!candidates.length) return null;
    // 路由指定了单元：只在该单元的配图里定位，绝不跳到其它单元
    if (selectedUnitId) {
      const onUnit = candidates.filter((c) => c.unitId === selectedUnitId);
      if (!onUnit.length) return null;
      if (selectedQaIndex != null) {
        const hit = onUnit.find((c) => c.qaIndex === selectedQaIndex);
        // 该题本身无配图：仍停在同单元第一张配图，便于对照；URL 由下方 effect 回写
        return hit || onUnit[0]!;
      }
      return onUnit[0]!;
    }
    if (localKey) {
      const hit = candidates.find((c) => c.key === localKey);
      if (hit) return hit;
    }
    return candidates[0]!;
  }, [candidates, selectedUnitId, selectedQaIndex, localKey]);

  // 仅在「无 unit」或「同 unit 需补 qa」时回写；绝不把路由改到别的单元
  useEffect(() => {
    if (!onSelectParticle || !selected) return;
    if (
      selectedUnitId === selected.unitId
      && selectedQaIndex === selected.qaIndex
    ) {
      return;
    }
    if (!selectedUnitId) {
      onSelectParticle(selected.unitId, selected.qaIndex);
      return;
    }
    if (selectedUnitId === selected.unitId && selectedQaIndex == null) {
      onSelectParticle(selected.unitId, selected.qaIndex);
      return;
    }
    if (
      selectedUnitId === selected.unitId
      && selectedQaIndex != null
      && selectedQaIndex !== selected.qaIndex
    ) {
      // 路由题号无配图 → 落到同单元第一张配图
      onSelectParticle(selected.unitId, selected.qaIndex);
    }
  }, [onSelectParticle, selected, selectedUnitId, selectedQaIndex]);

  // 侧栏滚到当前选中项
  useEffect(() => {
    if (!selected) return;
    const el = listItemRefs.current.get(selected.key);
    el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [selected?.key]);

  useEffect(() => {
    if (!selected) {
      setBoundParticleId(null);
      setEmbedKind(null);
      setClassifying(false);
      setCarouselIndex(0);
      return;
    }
    const cachedPid = particleIdByKey.current.get(selected.key) || null;
    setBoundParticleId(cachedPid);
    setEmbedKind(null);
    setCarouselIndex(0);
    if (cachedPid) void runEmbedClassifier(cachedPid);
  }, [selected?.key]);

  const carouselProviders = useMemo(() => {
    // 未分类前按 NGT（OI2/QI3）；GT 后走几何模块（与学习侧一致）
    const order = resolveModelOrder(embedKind || 'NGT');
    return order.map((id) => FIGURE_EDIT_PROVIDER_CATALOG[id]);
  }, [embedKind]);

  useEffect(() => {
    setCarouselIndex((i) => {
      if (!carouselProviders.length) return 0;
      return Math.min(i, carouselProviders.length - 1);
    });
  }, [carouselProviders]);

  const activeProvider = carouselProviders[carouselIndex] || carouselProviders[0] || null;

  const selectedIndex = selected
    ? candidates.findIndex((c) => c.key === selected.key)
    : -1;

  const box = selected?.figureBox;
  const hasBox = Boolean(box && (box.w > 0 || box.h > 0));

  useEffect(() => {
    let cancelled = false;
    setCropStatus('idle');
    setCropError('');
    setCropDataUrl('');
    setCropMeta(null);

    const cached = selected ? readCachedSlots(batchId, selected.key) : null;
    setSlots(cached || emptySlots());

    if (!selected) return;
    if (!hasBox || !box) {
      setCropStatus('error');
      setCropError('提取结果没有可用的 figure_box（坐标为空）');
      return;
    }

    setCropStatus('loading');
    const url = factoryService.structuralUnitPageImageUrl(batchId, selected.unitId);
    void cropPageImageByBox(url, box)
      .then((out) => {
        if (cancelled) return;
        setCropDataUrl(out.dataUrl);
        setCropMeta({
          pageW: out.pageW,
          pageH: out.pageH,
          crop: out.crop,
          boxUsed: out.boxUsed,
        });
        setCropStatus('ready');
        uploadedRefByKey.current.delete(selected.key);
      })
      .catch((err) => {
        if (cancelled) return;
        setCropStatus('error');
        setCropError(err instanceof Error ? err.message : String(err));
      });

    const factoryFigureKey = `${batchId}:${selected.unitId}:${selected.qaIndex}`;
    void dajiaoService.getFactoryFigure(factoryFigureKey)
      .then((data) => {
        if (cancelled) return;
        const particleId = String(data.particleId || '').trim();
        if (particleId) {
          particleIdByKey.current.set(selected.key, particleId);
          setBoundParticleId(particleId);
          try {
            const map: Record<string, string> = {};
            particleIdByKey.current.forEach((v, k) => {
              map[k] = v;
            });
            sessionStorage.setItem(
              `psycheflow.factoryFigureParticle.v1.${batchId}`,
              JSON.stringify(map),
            );
          } catch {
            // ignore
          }
          void runEmbedClassifier(particleId);
        }
        if (!data.candidates?.length) return;
        const hydrated = applyCandidateSlots(data.candidates);
        setSlots((prev) => {
          const merged = { ...prev };
          for (const id of ALL_PROVIDER_IDS) {
            const next = hydrated[id];
            if (next.status === 'ready' && next.pngUrl) {
              merged[id] = next;
              writeCachedSlot(batchId, selected.key, id, next);
            }
          }
          return merged;
        });
      })
      .catch(() => {
        // 回显失败不挡切块预览 / 试跑
      });

    return () => {
      cancelled = true;
    };
  }, [
    batchId,
    selected?.key,
    selected?.unitId,
    selected?.qaIndex,
    hasBox,
    box?.x,
    box?.y,
    box?.w,
    box?.h,
  ]);

  const ensureReferenceUploaded = async (candidate: FigureExtractCandidate, dataUrl: string) => {
    const cached = uploadedRefByKey.current.get(candidate.key);
    if (cached?.imageUrl) return cached;

    const userId = String(user?.id || '').trim();
    if (!userId) return null;

    try {
      const file = await dataUrlToJpegFile(
        dataUrl,
        `figure-extract-${candidate.unitId}-q${candidate.qaIndex + 1}.jpg`,
      );
      const asset = await assetService.uploadImageAsset({
        userId,
        file,
        description: `工厂插图提取试跑参考 · ${batchId.slice(0, 8)} · Q${candidate.qaIndex + 1}`,
        purpose: 'factory_figure',
      });
      const imageUrl = String(asset.url || '').trim();
      if (!imageUrl) return null;
      const payload = { imageUrl, ossId: asset.oss_id || undefined };
      uploadedRefByKey.current.set(candidate.key, payload);
      return payload;
    } catch {
      return null;
    }
  };

  const rememberParticleId = (candidateKey: string, particleId: string) => {
    const id = String(particleId || '').trim();
    if (!id) return;
    particleIdByKey.current.set(candidateKey, id);
    setBoundParticleId(id);
    try {
      const map: Record<string, string> = {};
      particleIdByKey.current.forEach((v, k) => {
        map[k] = v;
      });
      sessionStorage.setItem(
        `psycheflow.factoryFigureParticle.v1.${batchId}`,
        JSON.stringify(map),
      );
    } catch {
      // ignore
    }
  };

  const runTrial = async (providerId: FigureEditProviderId) => {
    if (!selected) return;
    if (cropStatus !== 'ready' || !cropDataUrl) {
      setSlots((prev) => ({
        ...prev,
        [providerId]: { status: 'error', error: '请先等待原切块预览就绪' },
      }));
      return;
    }

    // 原子快照：防止切换题目后仍用到旧切块/旧题干
    const snapshot = {
      key: selected.key,
      unitId: selected.unitId,
      qaIndex: selected.qaIndex,
      question: selected.question || selected.figureNote || '',
      cropDataUrl,
      factoryFigureKey: `${batchId}:${selected.unitId}:${selected.qaIndex}`,
      particleId: particleIdByKey.current.get(selected.key) || undefined,
      figureBox: selected.figureBox
        ? {
          x: selected.figureBox.x,
          y: selected.figureBox.y,
          w: selected.figureBox.w,
          h: selected.figureBox.h,
        }
        : undefined,
      boxUsed: cropMeta?.boxUsed
        ? {
          x: cropMeta.boxUsed.x,
          y: cropMeta.boxUsed.y,
          w: cropMeta.boxUsed.w,
          h: cropMeta.boxUsed.h,
        }
        : undefined,
      cropPixels: cropMeta
        ? {
          x: cropMeta.crop.x,
          y: cropMeta.crop.y,
          w: cropMeta.crop.w,
          h: cropMeta.crop.h,
        }
        : undefined,
      pageWidth: cropMeta?.pageW,
      pageHeight: cropMeta?.pageH,
    };

    const token = (runTokenByProvider.current[providerId] || 0) + 1;
    runTokenByProvider.current[providerId] = token;

    setSlots((prev) => ({
      ...prev,
      [providerId]: { status: 'running' },
    }));

    try {
      // 先上传切块到 OSS，再只传 URL（避免 imageDataUrl base64 触发网关 HTTP 413）
      const uploaded = await ensureReferenceUploaded(
        {
          key: snapshot.key,
          unitId: snapshot.unitId,
          unitIndex: selected.unitIndex,
          qaIndex: snapshot.qaIndex,
          question: snapshot.question,
        },
        snapshot.cropDataUrl,
      );
      if (runTokenByProvider.current[providerId] !== token) return;
      if (!uploaded?.imageUrl) {
        setSlots((prev) => ({
          ...prev,
          [providerId]: {
            status: 'error',
            error: '参考图上传失败，无法发起生图（请确认已登录）',
          },
        }));
        return;
      }

      const result = await dajiaoService.generateCandidates({
        imageUrl: uploaded.imageUrl,
        ossId: uploaded.ossId,
        questionHint: snapshot.question || undefined,
        factoryFigureKey: snapshot.factoryFigureKey,
        particleId: snapshot.particleId,
        models: [providerId],
        cropMeta: snapshot.figureBox && snapshot.boxUsed && snapshot.cropPixels
          && snapshot.pageWidth && snapshot.pageHeight
          ? {
            figureBox: snapshot.figureBox,
            boxUsed: snapshot.boxUsed,
            cropPixels: snapshot.cropPixels,
            pageWidth: snapshot.pageWidth,
            pageHeight: snapshot.pageHeight,
          }
          : undefined,
      });

      if (runTokenByProvider.current[providerId] !== token) return;
      if (selected.key !== snapshot.key) return;

      const persistedParticleId = String(result.particleId || '').trim();
      if (persistedParticleId) {
        rememberParticleId(snapshot.key, persistedParticleId);
        // 首次建 KP 或尚无 kind 时走学习侧分类，GT 则切出几何生图模块
        if (!embedKind) {
          void runEmbedClassifier(persistedParticleId);
        }
      }

      const attempt = (result.attempts || result.candidates || []).find(
        (item: GeometryCandidate) => item.id === providerId,
      ) || (result.attempts || result.candidates || [])[0];

      // 优先展示落库后的槽位 URL
      const persisted = (result.candidates || []).find(
        (item: GeometryCandidate) => item.id === providerId && item.pngUrl,
      );

      if (!attempt) {
        setSlots((prev) => ({
          ...prev,
          [providerId]: { status: 'error', error: '服务未返回该模型结果' },
        }));
        return;
      }

      const pngUrl = persisted?.pngUrl || attempt.pngUrl || undefined;
      if (attempt.error || !pngUrl) {
        setSlots((prev) => ({
          ...prev,
          [providerId]: {
            status: 'error',
            error: attempt.error || '生图失败（无预览图）',
            elapsedMs: attempt.elapsedMs,
            costCny: attempt.costCny,
            costEstimated: attempt.costEstimated,
          },
        }));
        return;
      }

      setSlots((prev) => ({
        ...prev,
        [providerId]: {
          status: 'ready',
          pngUrl,
          elapsedMs: attempt.elapsedMs,
          costCny: attempt.costCny ?? persisted?.costCny,
          costEstimated: attempt.costEstimated,
        },
      }));
      writeCachedSlot(batchId, snapshot.key, providerId, {
        status: 'ready',
        pngUrl,
        elapsedMs: attempt.elapsedMs,
        costCny: attempt.costCny ?? persisted?.costCny,
        costEstimated: attempt.costEstimated,
      });
    } catch (err) {
      if (runTokenByProvider.current[providerId] !== token) return;
      setSlots((prev) => ({
        ...prev,
        [providerId]: {
          status: 'error',
          error: err instanceof Error ? err.message : String(err),
        },
      }));
    }
  };

  if (!candidates.length) {
    return (
      <div className="rounded-xl border border-border bg-[#1a2028] p-4">
        <h3 className="text-sm font-semibold text-ink">插图提取 · 试验台</h3>
        <p className="mt-2 text-sm text-muted">
          当前任务尚无可切块的配图颗粒（需提取写出
          {' '}
          <code className="text-xs text-primary">has_problem_figure</code>
          {' '}
          与
          {' '}
          <code className="text-xs text-primary">figure_box</code>
          ）。题干文案推断的「疑似有配图」不会出现在此列表。
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <header className="shrink-0 rounded-xl border border-border bg-[#1a2028] p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-ink">插图提取 · 试验台</h3>
            <p className="mt-1 text-xs text-muted">
              仅当前任务（batch）提取确认的印刷配图；试跑结果写入知识颗粒 OI2/QI3 槽，返回本页会自动回显。
            </p>
          </div>
          <div className="shrink-0 rounded-lg border border-primary/30 bg-primary/10 px-2.5 py-1 text-[11px] font-semibold text-primary">
            可切块 {candidates.length}
          </div>
        </div>
      </header>

      <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-[minmax(0,280px)_minmax(0,1fr)] lg:overflow-hidden">
        <aside className="flex min-h-0 flex-col rounded-xl border border-border bg-[#1a2028] p-3 lg:h-full">
          <div className="mb-2 shrink-0 text-[11px] font-semibold uppercase tracking-wide text-muted">
            本任务 · 带插图颗粒
          </div>
          <ul className="min-h-0 flex-1 space-y-1 overflow-y-auto overscroll-contain">
            {candidates.map((c, i) => {
              const active = selected?.key === c.key;
              const kind = figureKindLabelZh(c.figureKind) || c.figureKind || '插图';
              return (
                <li key={c.key}>
                  <button
                    type="button"
                    ref={(el) => {
                      if (el) listItemRefs.current.set(c.key, el);
                      else listItemRefs.current.delete(c.key);
                    }}
                    onClick={() => selectCandidate(c)}
                    className={`w-full rounded-lg border px-2.5 py-2 text-left transition ${
                      active
                        ? 'border-primary/50 bg-primary/10'
                        : 'border-transparent hover:border-border hover:bg-white/[0.03]'
                    }`}
                  >
                    <div className="flex items-center gap-2 text-[11px] text-muted">
                      <span className="tabular-nums font-semibold text-primary">
                        #{i + 1}
                      </span>
                      <span>页单元 {c.unitIndex + 1} · Q{c.qaIndex + 1}</span>
                      <span className="ml-auto">{kind}</span>
                    </div>
                    <div className="mt-1 line-clamp-2 text-xs text-ink">
                      {c.question
                        ? <MathTextBlock inline text={c.question} className="text-xs" />
                        : '（无题干）'}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>

        <section className="min-h-0 space-y-3 overflow-y-auto overscroll-contain lg:h-full">
          {selected ? (
            <div className="rounded-xl border border-border bg-[#1a2028] p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm font-semibold text-ink">
                  当前 · 单元 {selected.unitIndex + 1} · Q{selected.qaIndex + 1}
                  {selectedIndex >= 0 ? (
                    <span className="ml-2 text-xs font-normal text-muted">
                      {selectedIndex + 1}/{candidates.length}
                    </span>
                  ) : null}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={selectedIndex <= 0}
                    onClick={() => {
                      const prev = candidates[selectedIndex - 1];
                      if (prev) selectCandidate(prev);
                    }}
                    className="inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1.5 text-xs disabled:opacity-40"
                  >
                    <ChevronLeft size={14} aria-hidden />
                    上一题
                  </button>
                  <button
                    type="button"
                    disabled={selectedIndex < 0 || selectedIndex >= candidates.length - 1}
                    onClick={() => {
                      const next = candidates[selectedIndex + 1];
                      if (next) selectCandidate(next);
                    }}
                    className="inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1.5 text-xs disabled:opacity-40"
                  >
                    下一题
                    <ChevronRight size={14} aria-hidden />
                  </button>
                </div>
              </div>
              <div className="mt-2 text-sm text-ink">
                {selected.question
                  ? <MathTextBlock text={selected.question} className="text-sm" />
                  : '（无题干）'}
              </div>
              {selected.figureNote ? (
                <p className="mt-2 text-xs text-muted">备注：{selected.figureNote}</p>
              ) : null}
              {box ? (
                <div className="mt-1 space-y-0.5 text-[11px] tabular-nums text-muted">
                  <p>
                    <span className="text-ink/80">提取 figure_box</span>
                    {' '}（模型标定，相对整页）· x={box.x.toFixed(3)} y={box.y.toFixed(3)}
                    {' '}w={box.w.toFixed(3)} h={box.h.toFixed(3)}
                  </p>
                  {cropMeta?.boxUsed ? (
                    <p>
                      <span className="text-ink/80">实际切图框</span>
                      {' '}（扩边后采用，投喂以此为准）· x={cropMeta.boxUsed.x.toFixed(3)} y={cropMeta.boxUsed.y.toFixed(3)}
                      {' '}w={cropMeta.boxUsed.w.toFixed(3)} h={cropMeta.boxUsed.h.toFixed(3)}
                    </p>
                  ) : null}
                  {cropMeta ? (
                    <p>
                      <span className="text-ink/80">像素框</span>
                      {' '}· {cropMeta.crop.w}×{cropMeta.crop.h} @ ({cropMeta.crop.x},{cropMeta.crop.y})
                      {' '}/ 页 {cropMeta.pageW}×{cropMeta.pageH}
                    </p>
                  ) : null}
                </div>
              ) : (
                <p className="mt-1 text-[11px] text-amber-200/90">
                  尚无 figure_box；请重新提取本页或先打开颗粒详情补全坐标。
                </p>
              )}

              <div className="mt-3 overflow-hidden rounded-lg border border-border/70 bg-[#12161c]">
                {cropStatus === 'loading' ? (
                  <div className="px-3 py-10 text-center text-xs text-muted">正在按坐标切页图…</div>
                ) : null}
                {cropStatus === 'error' ? (
                  <div className="px-3 py-10 text-center text-xs text-danger">
                    {cropError || '切图失败'}
                  </div>
                ) : null}
                {cropStatus === 'ready' && cropDataUrl ? (
                  <img
                    src={cropDataUrl}
                    alt="原切块预览"
                    className="mx-auto max-h-[min(42dvh,420px)] w-auto max-w-full object-contain"
                  />
                ) : null}
              </div>
            </div>
          ) : selectedUnitId ? (
            <div className="rounded-xl border border-border bg-[#1a2028] p-4">
              <p className="text-sm text-muted">
                当前单元没有可切块的印刷配图（提取未写出
                {' '}
                <code className="text-xs text-primary">figure_box</code>
                {' '}
                ，或仅为题干推断）。左侧列表不会跳到其它单元。
              </p>
            </div>
          ) : null}

          <div className="rounded-xl border border-border bg-[#1a2028] p-4">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <Sparkles size={14} className="text-primary" aria-hidden />
              <h4 className="text-sm font-semibold text-ink">Edit 生图对照</h4>
              <span className="text-[11px] text-muted">
                {classifying
                  ? '正在分类嵌入图类型…'
                  : embedKind === 'GT'
                    ? '几何（GT）· 大角置顶，左右切换对照其它备选'
                    : embedKind === 'NGT'
                      ? '非几何（NGT）· 左右切换生图模块'
                      : '未分类前按 NGT 展示 · 左右切换试跑'}
              </span>
              {boundParticleId ? (
                <span className="rounded border border-primary/30 bg-primary/10 px-2 py-0.5 text-[11px] text-primary">
                  已绑定颗粒 {boundParticleId.slice(0, 8)}…
                  {embedKind === 'GT' ? ' · 几何槽' : ' · OI2/QI3 槽'}
                </span>
              ) : (
                <span className="text-[11px] text-muted">试跑后写入知识颗粒嵌入图槽位</span>
              )}
            </div>

            {activeProvider ? (() => {
              const provider = activeProvider;
              const slot = slots[provider.id];
              const busy = slot.status === 'running';
              const canNav = carouselProviders.length > 1;
              return (
                <div className="flex flex-col rounded-xl border border-border/80 bg-canvas/20 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-ink">{provider.label}</div>
                      <p className="mt-1 text-xs text-muted">{provider.blurb}</p>
                    </div>
                    {canNav ? (
                      <div className="flex shrink-0 items-center gap-1">
                        <button
                          type="button"
                          aria-label="上一个生图模块"
                          onClick={() => {
                            setCarouselIndex((i) => {
                              const len = carouselProviders.length;
                              return (i - 1 + len) % len;
                            });
                          }}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border text-ink hover:bg-white/[0.04]"
                        >
                          <ChevronLeft size={16} aria-hidden />
                        </button>
                        <span className="min-w-[3.5rem] text-center text-[11px] tabular-nums text-muted">
                          {carouselIndex + 1}/{carouselProviders.length}
                        </span>
                        <button
                          type="button"
                          aria-label="下一个生图模块"
                          onClick={() => {
                            setCarouselIndex((i) => {
                              const len = carouselProviders.length;
                              return (i + 1) % len;
                            });
                          }}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border text-ink hover:bg-white/[0.04]"
                        >
                          <ChevronRight size={16} aria-hidden />
                        </button>
                      </div>
                    ) : null}
                  </div>

                  <div className="relative mt-3 flex min-h-[180px] items-center justify-center overflow-hidden rounded-lg border border-dashed border-border/70 bg-[#12161c]">
                    {busy ? (
                      <div className="flex flex-col items-center gap-2 px-3 py-6 text-xs text-muted">
                        <Loader2 size={18} className="animate-spin text-primary" aria-hidden />
                        生图中…
                      </div>
                    ) : null}
                    {!busy && slot.status === 'ready' && slot.pngUrl ? (
                      <ProxyImage
                        rawUrl={slot.pngUrl}
                        alt={`${provider.label} 生成预览`}
                        className="h-full w-full"
                        imgClassName="mx-auto max-h-[min(36dvh,280px)] w-auto max-w-full object-contain"
                        zoomable
                        zoomTitle={provider.label}
                      />
                    ) : null}
                    {!busy && slot.status === 'error' ? (
                      <div className="px-3 py-6 text-center text-xs text-danger">
                        {slot.error || '生图失败'}
                      </div>
                    ) : null}
                    {!busy && slot.status === 'idle' ? (
                      <div className="px-3 py-6 text-center text-[11px] text-muted">
                        生成预览位
                      </div>
                    ) : null}
                  </div>

                  {canNav ? (
                    <div className="mt-2 flex items-center justify-center gap-1.5">
                      {carouselProviders.map((p, idx) => {
                        const pSlot = slots[p.id];
                        const pBusy = pSlot.status === 'running';
                        return (
                          <button
                            key={p.id}
                            type="button"
                            aria-label={p.label}
                            title={pBusy ? `${p.label}（生图中）` : p.label}
                            onClick={() => setCarouselIndex(idx)}
                            className={`h-1.5 rounded-full transition ${
                              idx === carouselIndex
                                ? 'w-4 bg-primary'
                                : pBusy
                                  ? 'w-2.5 animate-pulse bg-primary/55'
                                  : 'w-1.5 bg-border hover:bg-muted'
                            }`}
                          />
                        );
                      })}
                    </div>
                  ) : null}

                  {(slot.elapsedMs != null || slot.costCny != null) && slot.status !== 'idle' ? (
                    <p className="mt-2 text-[11px] tabular-nums text-muted">
                      {slot.elapsedMs != null ? `${(slot.elapsedMs / 1000).toFixed(1)}s` : ''}
                      {slot.elapsedMs != null && slot.costCny != null ? ' · ' : ''}
                      {slot.costCny != null
                        ? `${slot.costEstimated ? '约' : ''}¥${slot.costCny.toFixed(3)}`
                        : ''}
                    </p>
                  ) : null}
                  <button
                    type="button"
                    disabled={!selected || cropStatus !== 'ready' || busy}
                    onClick={() => {
                      void runTrial(provider.id);
                    }}
                    className="mt-3 inline-flex min-h-9 items-center justify-center gap-1.5 rounded-lg bg-primary px-3 text-xs font-semibold text-white disabled:opacity-50"
                  >
                    {busy ? (
                      <>
                        <Loader2 size={14} className="animate-spin" aria-hidden />
                        生图中…
                      </>
                    ) : slot.status === 'ready' ? '再跑一次' : '试跑'}
                  </button>
                </div>
              );
            })() : (
              <p className="text-xs text-muted">暂无可用生图模块</p>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
